import { defaultProps } from "../../../../core/components/table/head/cell";
import { prefix } from "../../../../core/design-tokens/meta";
import { componentClasses } from "../../../../core/components/table/head/cell";
import { arrowDown, pin, overflow, unpin } from "../../../../core/js/helpers/built-in-icons";

/**
 * Creates the sort icon template
 * @returns {String} html containing the icon
 */
export const sortIconTemplate = () => {
    return arrowDown({ class: componentClasses.sortIcon });
};

/**
 * Creates the action menu icon template
 * @returns {String} html containing the icon
 */
export const actionMenuIconTemplate = () => {
    return overflow({ class: componentClasses.actionButton.icon });
};

/**
 * Creates the action menu pin/unpin button content
 * @param {Boolean} isPinned If it's pinned or not
 * @param {Object} labels Pin/unpin labels
 * @returns {String} Action menu pin/unpin button html content
 */
export const pinButtonContentTemplate = (isPinned, labels) => {
    const icon = isPinned
        ? unpin({ class: componentClasses.unpinIcon })
        : pin({ class: componentClasses.pinIcon });

    if (!labels) {
        labels = defaultProps.pin;
    }

    const label = isPinned ? labels.unpinLabel : labels.pinLabel;

    return `${icon}<span>${label}</span>`;
};

/**
 * Creates the pin button template to use on action menu
 * @param {Boolean} pinned if it is pinned or not
 * @param {Object} labels Pin/unpin labels
 * @returns {String} html containing the action menu pin button
 */
export const pinButtonTemplate = (pinned) => {
    const ariaChecked = `aria-checked="${pinned ? "true" : "false"}"`;

    return `<li class="dds__action-menu__item" role="none">
        <button type="button" role="menuitem" tabindex="1" ${ariaChecked} data-value="pin">
            ${pinButtonContentTemplate(pinned)}
        </button>
    </li>`;
};

/**
 * Create the separator template to use on action menu
 * @returns {String} html containing the action menu separator
 */
export const separatorTemplate = () =>
    `<li class="dds__action-menu__separator" role="separator"></li>`;

/**
 * Create the sort buttons template to use on action menu
 * @param {String} sortBy
 * @returns {String} html containing the action menu sort buttons
 */
export const sortButtonsTemplate = (sortBy) => {
    const sortButton = (props) => {
        const ariaChecked = `aria-checked="${props.checked ? "true" : "false"}"`;
        return `<li class="dds__action-menu__option" role="none">
            <button type="button" role="menuitemcheckbox" tabindex="1" ${ariaChecked} data-value="${props.value}">
                <span>${props.label}</span>
            </button>
        </li>`;
    };

    const sortButtons = Object.entries(defaultProps.sort.actions).reduce((acc, [value, label]) => {
        return acc + sortButton({ value, label, checked: sortBy === value });
    }, "");

    return `<li role="presentation"><ul role="group" aria-labelledby="actions-group">${sortButtons}</ul></li>`;
};

/**
 * Creates a cell containing an action button with the available options
 * passed by config
 * @param {{sortBy:string,value:string,pin:boolean,pinned:boolean}} config
 * @returns {String} html containing the action button template
 */
export const actionButtonTemplate = (config = {}) => {
    if (!config.sortBy && !config.pinnable) {
        return "";
    }

    const actionId = config.actionId;
    const classes = [componentClasses.actionButton.rest].filter(Boolean).join(" ");

    const sortTemplate = config.sortBy ? sortButtonsTemplate(config.sortBy) : "";
    const pinTemplate = config.pinnable ? pinButtonTemplate(config.pinned) : "";
    const separator = sortTemplate && pinTemplate ? separatorTemplate() : "";

    const template = `
    <div class="${prefix}action-menu ${prefix}action-menu--sm ${
        componentClasses.actionMenu
    }" data-dds="action-menu" data-trigger="#${actionId}">
        <button type="button" aria-describedby="${actionId}-sr" class="${classes}" id="${actionId}">
            ${sortIconTemplate()}
            <span class="${componentClasses.actionButton.label}">${config.value}</span>
            ${actionMenuIconTemplate()}
        </button>
        <div class="${prefix}action-menu__container ${prefix}action-menu__container--hidden" tabindex="-1" role="presentation" aria-hidden="true">
        <ul class="${prefix}action-menu__menu" role="menu" tabindex="-1">
            ${sortTemplate}
            ${separator}
            ${pinTemplate}
        </ul>
        </div>
    </div>`;

    return `${template}`;
};
